import { jsonrepair } from 'jsonrepair';
import React, { useEffect, useMemo, useState } from 'react';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { JiraIssueTypeData, JiraProjectData, JiraSprintData } from '../../../api/endpoints/jira';
import { deepCleanEmptyOrNull, fuzzySearch } from '../../../CodesThatsGottaGoSomewhereElse';
import { useSubmitter } from '../../../platform/Hook';
import { Button, Input } from '../../../ui';
import { ResultInfoForSubmitter } from '../../../ui/ResultInfo';
import { useResolve } from '../../Container';
import { Copy, Flexer } from '../../dashboard/elements/LayoutElements';
import { JiraProjectIssueTypePicker } from '../../packages/atlassian/menus/JiraProjectIssueTypePicker';
import { JiraSprintPicker } from '../../packages/atlassian/menus/JiraSprintPicker';

export type CreateJiraIssueViewProps = {
    jiraProject: JiraProjectData,
    prefill?: any,
    onComplete: (info: any) => void,
};

const pickIssueTypeFromPrefill = (jiraProject: JiraProjectData, prefill: any) => {
    if (!prefill?.issueType) return null;
    // Try exact match
    const exact = jiraProject.issueTypes.find(x => x.name.toLowerCase() === prefill.issueType.toLowerCase());
    if (exact) return exact;
    // Try fuzzy match
    const [fuzzy] = fuzzySearch({
        items: jiraProject.issueTypes,
        keys: ['name'],
        search: prefill?.issueType,
    });
    if (fuzzy) return fuzzy;
    // For now fail
    return null;
};

const pickIssueTypeFromString = (jiraProject: JiraProjectData, input: string) => {
    if (!input) return null;
    // Try exact match
    const exact = jiraProject.issueTypes.find(x => x.name.toLowerCase() === input.toLowerCase());
    if (exact) return exact;
    // Try fuzzy match
    const [fuzzy] = fuzzySearch({
        items: jiraProject.issueTypes,
        keys: ['name'],
        search: input,
    });
    if (fuzzy) return fuzzy;
    // For now fail
    return null;
};

import { useStreamedTextQuery } from '../../../api/ApiStreaming';
import { EditorSession } from '../../canvas/EditorSession';
import { Selection } from '../../canvas/Selection';

// export default function Page() {
//   const { completion, input, handleInputChange, handleSubmit } = useCompletion({
//     api: '/api/completion',
//   });

//   return (
//     <form onSubmit={handleSubmit}>
//       <input
//         name="prompt"
//         value={input}
//         onChange={handleInputChange}
//         id="input"
//       />
//       <button type="submit">Submit</button>
//       <div>{completion}</div>
//     </form>
//   );
// }

const useSelectionAsData = () => {
    const selection = useResolve(EditorSession).get(Selection);
    return useMemo(() => {
        return JSON.stringify(Array.from(selection).map(x => deepCleanEmptyOrNull(x.toJson())), null, 2);
    }, [selection]);
};

const useGenerated = () => {

    const [reply, triggerReply, done] = useStreamedTextQuery<{ messages: any }>('/llm.invoke?code=jiraCreate');
    const data = useSelectionAsData();

    // Start
    useEffect(() => {

        const messages = [
            { type: 'text', value: 'Write a Jira issue using this selection of virtual whiteboard objects:' },
            { type: 'text', value: data },
        ];

        triggerReply({ messages });
    }, []);

    return useMemo(() => {
        try {
            return [JSON.parse(jsonrepair(reply)), done] as const;
        }
        catch {
            return [{}, done] as const;
        }
    }, [reply, done]);
};

export const CreateJiraIssueView = ({ jiraProject, prefill, onComplete }: CreateJiraIssueViewProps) => {

    const api = useResolve(ApiClient);

    const [issueType, setIssueType] = useState(null as JiraIssueTypeData);
    const [sprint, setSprint] = useState(null as JiraSprintData);
    const [summary, setSummary] = useState('');
    const [description, setDescription] = useState('');

    const [onCreate, working, result] = useSubmitter(async () => {
        const resp = await api.exec(v2.jira.issues.create([{
            jiraProjectId: jiraProject.id,
            localId: 'N/A',
            summary,
            description,
            issueType,
            // sprint,
        }]));
        if (Array.isArray(resp) && resp[0]) {
            onComplete(resp[0]);
        }
    }, [issueType, summary, description]);

    useEffect(() => {
        setIssueType(pickIssueTypeFromPrefill(jiraProject, prefill));
        setSummary(prefill?.summary || prefill?.title || '');
        setDescription(prefill?.description || '');
    }, [jiraProject, prefill]);

    const [generated, done] = !!prefill ? [{}, true] : useGenerated();

    useEffect(() => {
        const { issue } = generated ?? {};
        if (issue?.issueType) setIssueType(pickIssueTypeFromString(jiraProject, issue.issueType));
        if (issue?.title) setSummary(issue.title);
        if (issue?.body) setDescription(issue.body);
    }, [generated]);

    return <Flexer flex="column" gap="1" width="64">
        <Flexer>
            <Copy>Issue type:</Copy>
            <JiraProjectIssueTypePicker jiraProjectId={jiraProject.id} selected={issueType} onChange={setIssueType} />
        </Flexer>
        <Flexer>
            <Copy>Issue type:</Copy>
            <JiraSprintPicker jiraProjectId={jiraProject.id} selected={sprint} onChange={setSprint} />
        </Flexer>
        <Flexer>
            <Copy>Summary:</Copy>
            <Input value={summary} onChange={setSummary} />
        </Flexer>
        <Flexer>
            <Copy>Description:</Copy>
            <Input multiline={{ resize: 'vertical', rows: 15 }} value={description} onChange={setDescription} />
        </Flexer>
        <Flexer>
            <Button variant="primary" onAction={onCreate} disabled={working}>Create Issue</Button>
            <ResultInfoForSubmitter result={result} />
        </Flexer>
    </Flexer>;
};
